import React from 'react';
import { X, PenLine, Trash2, Check, Sparkles, BookOpen, RotateCcw } from 'lucide-react';

interface AnnotationEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (noteText: string, colorHex: string) => Promise<void>;
  selectedText: string;
  pageNumber: number;
  structuralReference?: string | null;
  initialNoteText?: string;
  initialColorHex?: string;
  isEditing?: boolean;
  onDelete?: () => Promise<void>;
  onRetryBox?: () => void;
  onColorChange?: (colorHex: string) => void;
}

const COLOR_PALETTE = [
  { hex: '#F59E0B', label: 'Ambre', desc: 'Remarques & points clés' },
  { hex: '#10B981', label: 'Émeraude', desc: 'Validations & favorable' },
  { hex: '#3B82F6', label: 'Saphir', desc: 'Doctrine & références' },
  { hex: '#F43F5E', label: 'Rose', desc: 'Risques & vigilance' },
  { hex: '#8B5CF6', label: 'Pourpre', desc: 'Jurisprudence & synthèse' },
];

export const AnnotationEditorModal: React.FC<AnnotationEditorModalProps> = ({
  isOpen,
  onClose,
  onSave,
  selectedText,
  pageNumber,
  structuralReference,
  initialNoteText = '',
  initialColorHex = '#F59E0B',
  isEditing = false,
  onDelete,
  onRetryBox,
  onColorChange,
}) => {
  const [noteText, setNoteText] = React.useState(initialNoteText);
  const [colorHex, setColorHex] = React.useState(initialColorHex);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    if (isOpen) {
      setNoteText(initialNoteText);
      setColorHex(initialColorHex || '#F59E0B');
      setShowDeleteConfirm(false);
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
  }, [isOpen, initialNoteText, initialColorHex]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (isSaving) return;
    try {
      setIsSaving(true);
      await onSave(noteText.trim(), colorHex);
      onClose();
    } catch (err) {
      console.error('Erreur lors de la sauvegarde de l\'annotation:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete || isDeleting) return;
    try {
      setIsDeleting(true);
      await onDelete();
      onClose();
    } catch (err) {
      console.error('Erreur lors de la suppression de l\'annotation:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
    } else if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10002] flex items-center justify-center p-4 bg-zinc-950/40 backdrop-blur-[1px] animate-in fade-in duration-200"
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-xl bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl border border-zinc-200 dark:border-zinc-800 overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between bg-zinc-50/70 dark:bg-zinc-900/70">
          <div className="flex items-center gap-2.5">
            <div
              className="w-8 h-8 rounded-xl flex items-center justify-center shadow-xs"
              style={{ backgroundColor: `${colorHex}20`, color: colorHex }}
            >
              <PenLine className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
                <span>{isEditing ? 'Modifier l’annotation' : 'Nouvelle annotation contextuelle'}</span>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-zinc-200 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300">
                  Page {pageNumber}
                </span>
                {structuralReference && (
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 flex items-center gap-1">
                    <BookOpen className="w-2.5 h-2.5" />
                    {structuralReference}
                  </span>
                )}
              </h3>
              <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
                Visible uniquement depuis votre espace personnel
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition cursor-pointer"
            title="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
          {/* Passage sélectionné */}
          {selectedText && (
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-zinc-600 dark:text-zinc-400 uppercase tracking-wider flex items-center gap-1.5">
                <Sparkles className="w-3 h-3 text-amber-500" />
                <span>Extrait juridique ciblé</span>
              </label>
              <div
                className="p-3 rounded-xl bg-amber-50/40 dark:bg-amber-950/20 border-l-4 text-xs font-serif leading-relaxed text-zinc-800 dark:text-zinc-200 max-h-28 overflow-y-auto select-text"
                style={{ borderLeftColor: colorHex }}
              >
                « {selectedText} »
              </div>
            </div>
          )}

          {/* Palette de couleur */}
          <div className="space-y-2">
            <label className="text-[11px] font-bold text-zinc-600 dark:text-zinc-400 uppercase tracking-wider flex items-center justify-between">
              <span>Couleur du surlignage & encadrement</span>
              <span className="text-[10px] text-zinc-400 font-normal">Cliquez pour appliquer</span>
            </label>
            
            <div className="flex items-center gap-3 pt-1">
              {COLOR_PALETTE.map((c) => {
                const isSelected = colorHex === c.hex;
                return (
                  <button
                    key={c.hex}
                    type="button"
                    onClick={() => {
                      setColorHex(c.hex);
                      onColorChange?.(c.hex);
                    }}
                    className={`relative w-9 h-9 rounded-full transition-all duration-150 flex items-center justify-center cursor-pointer ${
                      isSelected
                        ? 'ring-4 ring-offset-2 ring-zinc-950 dark:ring-white scale-110 shadow-lg'
                        : 'opacity-70 hover:opacity-100 hover:scale-105 shadow-xs border border-black/10 dark:border-white/10'
                    }`}
                    style={{ backgroundColor: c.hex }}
                    title={`${c.label} (${c.desc})`}
                  >
                    {isSelected && (
                      <div className="w-full h-full rounded-full border-2 border-white/90 flex items-center justify-center">
                        <Check className="w-4 h-4 text-white drop-shadow-md stroke-[3.5]" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Carte récapitulative bien visible de la couleur active (clair & foncé) */}
            <div
              className="flex items-center justify-between px-3 py-1.5 rounded-xl border transition-all mt-1"
              style={{
                backgroundColor: `${colorHex}15`,
                borderColor: `${colorHex}60`,
              }}
            >
              <div className="flex items-center gap-2">
                <span
                  className="w-3.5 h-3.5 rounded-full shadow-xs border border-white"
                  style={{ backgroundColor: colorHex }}
                />
                <span className="text-xs font-bold" style={{ color: colorHex }}>
                  {COLOR_PALETTE.find((c) => c.hex === colorHex)?.label}
                </span>
                <span className="text-[11px] text-zinc-600 dark:text-zinc-300 font-medium">
                  — {COLOR_PALETTE.find((c) => c.hex === colorHex)?.desc}
                </span>
              </div>
              <span
                className="text-[8.5px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full text-white shadow-xs"
                style={{ backgroundColor: colorHex }}
              >
                Actif
              </span>
            </div>
          </div>

          {/* Zone de note */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label htmlFor="annotation-note-input" className="text-[11px] font-bold text-zinc-600 dark:text-zinc-400 uppercase tracking-wider">
                Note de travail / Observation juridique
              </label>
              <span className="text-[10px] font-mono text-zinc-400">
                {noteText.length} / 2000
              </span>
            </div>
            <textarea
              id="annotation-note-input"
              ref={textareaRef}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value.slice(0, 2000))}
              placeholder="Indiquez vos remarques, analyse juridique, renvois de jurisprudence ou éléments d'attention sur ce texte..."
              rows={4}
              className="w-full text-xs text-zinc-900 dark:text-zinc-100 bg-zinc-50 dark:bg-zinc-800/80 border border-zinc-300 dark:border-zinc-700 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 transition-all resize-y placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3.5 border-t border-zinc-200 dark:border-zinc-800 bg-zinc-50/70 dark:bg-zinc-900/70 flex items-center justify-between">
          <div>
            {isEditing && onDelete && (
              showDeleteConfirm ? (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-rose-600 font-semibold">Confirmer ?</span>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="px-2.5 py-1 text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 rounded-lg transition cursor-pointer"
                  >
                    {isDeleting ? 'Suppression...' : 'Oui, supprimer'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-2 py-1 text-xs text-zinc-600 hover:text-zinc-900 transition cursor-pointer"
                  >
                    Non
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center gap-1.5 text-xs text-rose-600 hover:text-rose-700 font-semibold hover:bg-rose-50 dark:hover:bg-rose-950/30 px-2.5 py-1.5 rounded-lg transition cursor-pointer"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Supprimer</span>
                </button>
              )
            )}
          </div>

          <div className="flex items-center gap-2">
            {onRetryBox && !isEditing && (
              <button
                type="button"
                onClick={onRetryBox}
                disabled={isSaving}
                className="flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold text-amber-700 dark:text-amber-400 hover:bg-amber-500/10 border border-amber-500/50 rounded-xl transition cursor-pointer"
                title="Fermer cette modale et recommencer le tracé de l'encadré sur la page"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>Recommencer l'encadré</span>
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-1.5 text-xs font-bold text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-800 rounded-xl transition cursor-pointer"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              style={{
                backgroundColor: colorHex,
                boxShadow: `0 4px 14px ${colorHex}50`,
              }}
              className="flex items-center gap-1.5 px-5 py-1.5 text-xs font-bold text-white rounded-xl transition shadow-sm active:scale-95 cursor-pointer disabled:opacity-50"
            >
              <Check className="w-3.5 h-3.5 stroke-[3]" />
              <span>{isSaving ? 'Enregistrement...' : 'Enregistrer'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
